import { createFileRoute, redirect } from '@tanstack/react-router'
import { LoginForm } from '@/features/auth/login-form'
import { currentUserQuery } from '@/lib/auth'

/**
 * Route shell. Definition and guards only — the form lives in a feature.
 *
 * The `beforeLoad` is `_authenticated`'s guard in reverse: a valid session
 * bounces off `/login` to the dashboard. It is load-bearing, not ceremony —
 * submitting the sign-in form while a session exists makes Fortify's
 * `guest`-middleware 302 `POST /login` to the app root, and the fetch follows
 * that redirect with `Accept: application/json`, which the dev server answers
 * with a 404. The sign-in then fails for the wrong reason: not the
 * credentials, the redirect. A second account is tested by signing out first;
 * this guard makes that explicit instead of a 404.
 */
export const Route = createFileRoute('/login')({
  beforeLoad: async ({ context, location, search }) => {
    // Idle expiry removed the current-user query; don't immediately refetch it
    // while navigating to the login form.
    if (search.reason === 'idle' && location.state.idleExpiryRedirect) return

    let authenticated = false
    try {
      await context.queryClient.ensureQueryData(currentUserQuery)
      authenticated = true
    } catch {
      // Login is the reverse guard: any session-check failure means the form
      // must remain available, including transient and server-side failures.
      authenticated = false
    }
    if (authenticated) {
      throw redirect({ to: '/' })
    }
  },
  component: LoginForm,
  validateSearch: (
    search: Record<string, unknown>,
  ): { redirect?: string; reason?: 'idle'; email?: string } => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
    reason: search.reason === 'idle' ? 'idle' : undefined,
    email: typeof search.email === 'string' ? search.email : undefined,
  }),
})
